import React from "react";
import { adminApi } from "../lib/api";
import { Inbox, AlertTriangle, Users, Building2, FileText, MessageCircle, RefreshCw, Zap } from "lucide-react";

const TYPE_ICONS = {
  driver_approval: Users,
  business_lead: Building2,
  sc_invoice: FileText,
  urgent: AlertTriangle,
  contact: MessageCircle,
};

const TYPE_COLORS = {
  driver_approval: "text-blue-600 bg-blue-50",
  business_lead: "text-purple-600 bg-purple-50",
  sc_invoice: "text-amber-600 bg-amber-50",
  urgent: "text-red-600 bg-red-50",
  contact: "text-slate-600 bg-slate-50",
};

export default function InboxPanel({ onTabChange }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await adminApi().get("/admin/inbox");
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
    const t = setInterval(load, 60000); // auto-refresh every minute
    return () => clearInterval(t);
  }, [load]);

  if (loading && !data) return <div className="text-brand-text2">Indlæser inbox...</div>;
  if (error) return <div className="rounded-xl border border-red-300 bg-red-50 p-3 text-red-800">{error}</div>;
  if (!data) return null;

  const c = data.counts;

  return (
    <div className="space-y-5" data-testid="inbox-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-brand-dark flex items-center gap-2">
            <Inbox size={20} className="text-brand-red" />
            Inbox
          </h2>
          <p className="text-sm text-brand-text2 mt-1">
            {c.total_actionable === 0 ? (
              <span className="text-green-700 font-semibold">🎉 Alt er under kontrol — ingen handlinger venter</span>
            ) : (
              <><strong className="text-brand-red">{c.total_actionable}</strong> ting venter din opmærksomhed</>
            )}
          </p>
        </div>
        <button
          onClick={load}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50 inline-flex items-center gap-1.5"
          data-testid="inbox-refresh"
        >
          <RefreshCw size={14} /> Opdater
        </button>
      </div>

      {/* Count cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <CountCard
          icon={<AlertTriangle size={18} />}
          label="Akutte"
          value={c.active_urgent}
          tone="red"
          onClick={() => onTabChange && onTabChange("urgent")}
          testid="inbox-count-urgent"
        />
        <CountCard
          icon={<Users size={18} />}
          label="Chauffør-godkendelser"
          value={c.pending_drivers}
          tone="blue"
          onClick={() => onTabChange && onTabChange("pending_drivers")}
          testid="inbox-count-drivers"
        />
        <CountCard
          icon={<Building2 size={18} />}
          label="Nye B2B leads"
          value={c.new_leads}
          tone="purple"
          onClick={() => onTabChange && onTabChange("business_leads")}
          testid="inbox-count-leads"
        />
        <CountCard
          icon={<FileText size={18} />}
          label="Vognmandsfakturaer"
          value={c.pending_sc_invoices}
          tone="amber"
          onClick={() => onTabChange && onTabChange("vognmaend")}
          testid="inbox-count-sc-invoices"
        />
        <CountCard
          icon={<MessageCircle size={18} />}
          label="Ulæste beskeder"
          value={c.unread_contacts}
          tone="slate"
          onClick={() => onTabChange && onTabChange("messages")}
          testid="inbox-count-contacts"
        />
        <CountCard
          icon={<Zap size={18} />}
          label="Aktive bøder"
          value={c.active_penalties}
          tone="slate"
          onClick={() => onTabChange && onTabChange("training")}
          testid="inbox-count-penalties"
        />
        <CountCard
          icon={<Zap size={18} />}
          label="Afventende træninger"
          value={c.pending_trainings}
          tone="slate"
          onClick={() => onTabChange && onTabChange("training")}
          testid="inbox-count-trainings"
        />
        <CountCard
          icon={<MessageCircle size={18} />}
          label="Live-chat aktivitet (7d)"
          value={c.recent_chats}
          tone="slate"
          testid="inbox-count-chat"
        />
      </div>

      {/* Feed */}
      <div className="rounded-2xl border border-slate-200 bg-white">
        <div className="px-5 py-3 border-b border-slate-200 font-display font-bold text-brand-dark">
          Seneste handlinger (sorteret efter prioritet)
        </div>
        {data.feed.length === 0 ? (
          <div className="p-8 text-center text-brand-text2" data-testid="inbox-empty">
            Intet venter på handling — godt arbejde 🎉
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="inbox-feed">
            {data.feed.map((item, i) => {
              const Icon = TYPE_ICONS[item.type] || Inbox;
              const colorClass = TYPE_COLORS[item.type] || "text-slate-600 bg-slate-50";
              return (
                <li key={`${item.type}-${item.id}-${i}`} className="px-5 py-3 hover:bg-slate-50 transition-colors" data-testid={`inbox-item-${item.type}-${item.id}`}>
                  <button
                    onClick={() => onTabChange && onTabChange(item.tab)}
                    className="w-full flex items-start gap-3 text-left"
                  >
                    <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${colorClass}`}>
                      <Icon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-brand-dark text-sm">{item.title}</div>
                      {item.subtitle && <div className="text-xs text-brand-text2 mt-0.5 truncate">{item.subtitle}</div>}
                    </div>
                    <div className="shrink-0 text-xs text-brand-text2">{(item.ts || "").slice(0, 16).replace("T", " ")}</div>
                    {item.priority >= 4 && (
                      <span className="shrink-0 inline-block rounded-md bg-red-100 text-red-800 px-2 py-0.5 text-xs font-bold">AKUT</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function CountCard({ icon, label, value, tone = "slate", onClick, testid }) {
  const tones = {
    red: "border-red-200 bg-red-50 hover:bg-red-100",
    blue: "border-blue-200 bg-blue-50 hover:bg-blue-100",
    purple: "border-purple-200 bg-purple-50 hover:bg-purple-100",
    amber: "border-amber-200 bg-amber-50 hover:bg-amber-100",
    slate: "border-slate-200 bg-white hover:bg-slate-50",
  };
  const Component = onClick ? "button" : "div";
  return (
    <Component
      onClick={onClick}
      className={`rounded-2xl border p-4 text-left transition-colors ${tones[tone]} ${onClick ? "cursor-pointer" : ""}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs uppercase text-brand-text2 leading-tight">{label}</span>
        <span className="text-brand-text2">{icon}</span>
      </div>
      <div className={"mt-2 text-2xl font-bold " + (value > 0 ? "text-brand-dark" : "text-slate-400")}>{value ?? 0}</div>
    </Component>
  );
}
